import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Card, Group, Modal, PasswordInput, Stack, Switch, Text, TextInput } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconCheck, IconMail, IconMessageCircle, IconX } from '@tabler/icons-react'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { Controller, FieldErrors, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useChangePasswordMutation, useGetMe, useUpdateUserSettingsMutation } from '../../../api/user/api'
import { PhoneNumberInput } from '../../../components/PhoneNumberInput/PhoneNumberInput'
import { phoneNumberSchema, removeCountryCode } from '../../../components/PhoneNumberInput/phoneNumberUtils'
import { PasswordValidationRules, validatePassword } from '../../../utils/password-validation'
import { removePhoneNumberFormatting } from '../../../utils/phone-number-formatting'

const userSettingsSchema = z.object({
  first_name: z.string().min(1, 'First name is required').max(50, 'First name must be 50 characters or less'),
  last_name: z.string().min(1, 'Last name is required').max(50, 'Last name must be 50 characters or less'),
  mobile_phone_number: z.string().nullable(),
  email_notification: z.boolean(),
  text_notifications_enabled: z.boolean(),
})

const passwordSchema = z
  .object({
    old_password: z.string().min(1, 'Current password is required'),
    new_password: z
      .string()
      .min(
        PasswordValidationRules.minLength,
        `Password must be at least ${PasswordValidationRules.minLength} characters long`,
      )
      .max(
        PasswordValidationRules.maxLength,
        `Password must be ${PasswordValidationRules.maxLength} characters or less`,
      )
      .regex(PasswordValidationRules.hasUpperCase, 'Password must contain at least one uppercase letter')
      .regex(PasswordValidationRules.hasLowerCase, 'Password must contain at least one lowercase letter')
      .regex(PasswordValidationRules.hasNumber, 'Password must contain at least one number')
      .regex(PasswordValidationRules.hasSpecialChar, 'Password must contain at least one special character'),
    confirm_password: z.string(),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  })

type UserSettingsFormData = z.infer<typeof userSettingsSchema>
type PasswordFormData = z.infer<typeof passwordSchema>

export const Route = createFileRoute('/app/settings/user-settings')({
  component: UserSettingsComponent,
})

function UserSettingsComponent() {
  const { data: user, isLoading: userLoading, isError: userError } = useGetMe()
  const [passwordModalOpened, { open: openPasswordModal, close: closePasswordModal }] = useDisclosure(false)

  const updateUserSettingsMutation = useUpdateUserSettingsMutation()
  const changePasswordMutation = useChangePasswordMutation()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    watch,
    setValue,
  } = useForm<UserSettingsFormData>({
    resolver: zodResolver(userSettingsSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      mobile_phone_number: null,
      email_notification: false,
      text_notifications_enabled: false,
    },
  })

  const {
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    watch: watchPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      old_password: '',
      new_password: '',
      confirm_password: '',
    },
  })

  useEffect(() => {
    if (user) {
      reset({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        mobile_phone_number: user.mobile_phone_number ? removeCountryCode(user.mobile_phone_number) : null,
        email_notification: user.email_notification || false,
        text_notifications_enabled: user.text_notifications_enabled || false,
      })
    }
  }, [user, reset])

  const onSubmit = (data: UserSettingsFormData) => {
    if (user) {
      updateUserSettingsMutation.mutate({
        userId: user.id,
        formData: {
          firstName: data.first_name,
          lastName: data.last_name,
          phone: data.mobile_phone_number ? removePhoneNumberFormatting(data.mobile_phone_number) : null,
          emailNotification: data.email_notification,
          textNotificationsEnabled: data.text_notifications_enabled,

          // These values are not edited by the user, but are required by the backend PUT endpoint
          branchIds: user.branchInfo?.map((branch) => branch.provider_branch_id) || [],
          role: user.role,
          email: user.email || null,
        },
      })
    }
  }

  const onPasswordSubmit = (data: PasswordFormData) => {
    if (user) {
      changePasswordMutation.mutate(
        {
          userId: user.id,
          data: {
            old_password: data.old_password,
            new_password: data.new_password,
          },
        },
        {
          onSuccess: () => {
            resetPassword()
            closePasswordModal()
          },
        },
      )
    }
  }

  const hasValidPhoneNumber =
    watch('mobile_phone_number') &&
    watch('mobile_phone_number')?.trim() !== '' &&
    phoneNumberSchema.safeParse(watch('mobile_phone_number')).success

  useEffect(() => {
    if (hasValidPhoneNumber) {
      setValue('text_notifications_enabled', true)
    }
    if (!hasValidPhoneNumber) {
      setValue('text_notifications_enabled', false)
    }
  }, [hasValidPhoneNumber, setValue])

  return (
    <>
      <Stack className="user-settings-content" maw="750px" mx="auto" mt="xl">
        <Card>
          <Stack>
            <Controller
              name="first_name"
              control={control}
              render={({ field }) => (
                <Box pos="relative">
                  <TextInput
                    {...field}
                    label="First Name"
                    placeholder="Enter your first name"
                    disabled={userLoading || userError}
                  />
                  {errors.first_name?.message && (
                    <div className="absolute-error-message">{errors.first_name.message}</div>
                  )}
                </Box>
              )}
            />
            <Controller
              name="last_name"
              control={control}
              render={({ field }) => (
                <Box pos="relative">
                  <TextInput
                    {...field}
                    label="Last Name"
                    placeholder="Enter your last name"
                    disabled={userLoading || userError}
                  />
                  {errors.last_name?.message && (
                    <div className="absolute-error-message">{errors.last_name.message}</div>
                  )}
                </Box>
              )}
            />

            <TextInput label="Email" value={user?.email || ''} disabled />

            <Controller
              name="mobile_phone_number"
              control={control}
              render={({ field }) => (
                <Box pos="relative">
                  <PhoneNumberInput
                    value={field.value || ''}
                    onChange={field.onChange}
                    disabled={userLoading || userError}
                    required={user?.role === 'SUPERADMIN' || user?.role === 'ADMIN' || !user?.email}
                  />
                  {errors.mobile_phone_number?.message && (
                    <div className="absolute-error-message">{errors.mobile_phone_number.message}</div>
                  )}
                </Box>
              )}
            />

            <Group justify="space-between" style={{ borderBottom: '1px solid #EAECF0', paddingBottom: '16px' }}>
              <Text size="sm" fw={500} mb="xs">
                Notifications
              </Text>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Group gap="xs">
                    <IconMessageCircle size={20} />
                    <Text size="sm">Text</Text>
                  </Group>
                  <Controller
                    name="text_notifications_enabled"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={userLoading || userError || !hasValidPhoneNumber}
                      />
                    )}
                  />
                </Group>
                <Group justify="space-between">
                  <Group gap="xs">
                    <IconMail size={20} />
                    <Text size="sm">Email</Text>
                  </Group>
                  <Controller
                    name="email_notification"
                    control={control}
                    render={({ field }) => (
                      <Switch checked={field.value} onChange={field.onChange} disabled={userLoading || userError} />
                    )}
                    disabled={!user?.email}
                  />
                </Group>
              </Stack>
            </Group>

            <Group justify="space-between" style={{ borderBottom: '1px solid #EAECF0', paddingBottom: '16px' }}>
              <Text size="sm" fw={500}>
                Password
              </Text>
              <Button
                variant="subtle"
                size="xs"
                onClick={openPasswordModal}
                c="orange"
                disabled={userLoading || userError}
              >
                Change password
              </Button>
            </Group>

            <Group justify="flex-end">
              <Button
                onClick={handleSubmit(onSubmit)}
                loading={updateUserSettingsMutation.isPending}
                disabled={userLoading || userError || !isDirty}
              >
                Save changes
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
      <ChangePasswordModal
        isOpen={passwordModalOpened}
        close={closePasswordModal}
        newPassword={watchPassword('new_password')}
        confirmPassword={watchPassword('confirm_password')}
        oldPassword={watchPassword('old_password')}
        onChange={(data) => {
          resetPassword({
            old_password: data.oldPassword,
            new_password: data.newPassword,
            confirm_password: data.confirmPassword,
          })
        }}
        handleSubmit={handlePasswordSubmit(onPasswordSubmit)}
        submitLoading={changePasswordMutation.isPending}
        errors={passwordErrors}
      />
    </>
  )
}

type ChangePasswordData = {
  isOpen: boolean
  close: () => void
  newPassword: string
  confirmPassword: string
  oldPassword: string
  onChange: ({
    oldPassword,
    newPassword,
    confirmPassword,
  }: {
    oldPassword: string
    newPassword: string
    confirmPassword: string
  }) => void
  handleSubmit: () => void
  submitLoading: boolean
  errors: FieldErrors<PasswordFormData>
}

const ChangePasswordModal = ({
  isOpen,
  close,
  newPassword,
  confirmPassword,
  oldPassword,
  onChange,
  handleSubmit,
  submitLoading,
  errors,
}: ChangePasswordData) => {
  const passwordValidation = validatePassword(newPassword)
  const isPasswordValid = Object.values(passwordValidation).every(Boolean)
  const passwordsMatch = newPassword === confirmPassword

  return (
    <Modal opened={isOpen} onClose={close} title="Change password" size="md">
      <Stack>
        <PasswordInput
          label="Current password"
          placeholder="Enter current password"
          value={oldPassword}
          onChange={(e) =>
            onChange({ oldPassword: e.target.value, newPassword: newPassword, confirmPassword: confirmPassword })
          }
          required
        />

        <PasswordInput
          label="New password"
          placeholder="Enter new password"
          value={newPassword}
          onChange={(e) =>
            onChange({ newPassword: e.target.value, confirmPassword: confirmPassword, oldPassword: oldPassword })
          }
          required
        />

        <PasswordInput
          label="Confirm new password"
          placeholder="Repeat new password"
          value={confirmPassword}
          onChange={(e) =>
            onChange({ confirmPassword: e.target.value, newPassword: newPassword, oldPassword: oldPassword })
          }
          required
          error={errors.confirm_password?.message}
        />

        <Stack gap="xs">
          <Group gap="xs">
            {passwordValidation.length ? <IconCheck size={14} color="green" /> : <IconX size={14} color="gray" />}
            <Text size="sm" c={passwordValidation.length ? 'green' : 'dimmed'}>
              Must be between 8 and 32 characters long
            </Text>
          </Group>
          <Group gap="xs">
            {passwordValidation.hasUpperCase && passwordValidation.hasLowerCase ? (
              <IconCheck size={14} color="green" />
            ) : (
              <IconX size={14} color="gray" />
            )}
            <Text size="sm" c={passwordValidation.hasUpperCase && passwordValidation.hasLowerCase ? 'green' : 'dimmed'}>
              Must contain 1 uppercase and 1 lowercase letter
            </Text>
          </Group>
          <Group gap="xs">
            {passwordValidation.hasSpecialChar ? (
              <IconCheck size={14} color="green" />
            ) : (
              <IconX size={14} color="gray" />
            )}
            <Text size="sm" c={passwordValidation.hasSpecialChar ? 'green' : 'dimmed'}>
              Must contain 1 special character (!, @, #, $, %, etc.)
            </Text>
          </Group>
          <Group gap="xs">
            {passwordValidation.hasNumber ? <IconCheck size={14} color="green" /> : <IconX size={14} color="gray" />}
            <Text size="sm" c={passwordValidation.hasNumber ? 'green' : 'dimmed'}>
              Must contain 1 number
            </Text>
          </Group>
        </Stack>

        <Group justify="flex-end">
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={submitLoading}
            disabled={!isPasswordValid || !passwordsMatch || !oldPassword}
          >
            Confirm
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
